import { StaticImage, IGatsbyImageData, GatsbyImage } from "gatsby-plugin-image";
import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide, SwiperClass } from "swiper/react";
import { Autoplay } from 'swiper/modules';
import { h1, h3, h5 } from "../ui/typography/typography.module.css";
import {
  slideContainer,
  avatarContainer,
  testimonialContainer,
  clientInfoContainer
} from "./testimonials.module.css";

import "swiper/css";
import TextLabel from "@components/ui/text-label";
import Section from "@layouts/section";
import { graphql, useStaticQuery } from "gatsby";
import { getWindowDimensions } from "@/utils";
import config from "tailwindcss/defaultConfig";

interface Testimonial {
  id: string;
  name: string;
  content: string;
  position: string;
  avatar: {
    localFile: {
      childImageSharp: {
        gatsbyImageData: IGatsbyImageData;
      };
    };
    alternativeText: string;
  };
  clientLogo: {
    localFile: {
      publicURL: string;
    };
    alternativeText: string;
  };
}

interface TestimonialsQuery {
  allStrapiTestimonial: {
    nodes: Testimonial[];
  };
  allStrapiPageContent: {
    nodes: Content[];
  };
}
interface Content {
  testimonialsLabel: string;
  testimonialsTitle: string;
}

interface TestimonailsProps extends React.HTMLProps<HTMLDivElement> {
  title?: string;
  label?: string;
}

const Testimonails: React.FC<TestimonailsProps> = ({
  className,
  title = "No sugarcoating. Straight from our customers' mouths to your ears.",
  label = "Testimonials"
}) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [swiperDirection, setSwiperDirection] = useState<"vertical" | "horizontal">("vertical");

  useEffect(() => {
    function handleResize() {
      const { width, height } = getWindowDimensions();
      const mdBreakpoint = (config.theme?.screens as Record<string, string>).md;
      if (mdBreakpoint) {
        const mdBreakpointPx = parseInt(mdBreakpoint.replace(/[^0-9]/g, ''));
        setSwiperDirection(width >= mdBreakpointPx && height < width ? "vertical" : "horizontal");
      }
    }
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const data: TestimonialsQuery = useStaticQuery(graphql`
    query testimonial {
      allStrapiTestimonial {
        nodes {
          id
          name
          content
          position
          avatar {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 800, placeholder: BLURRED)
              }
            }
            alternativeText
          }
          clientLogo {
            localFile {
              publicURL
            }
            alternativeText
          }
        }
      }
      allStrapiPageContent {
        nodes {
          testimonialsLabel
          testimonialsTitle
        }
      }
    }
  `);
  const testimonials = data.allStrapiTestimonial.nodes;
  const testimonialHeader = data.allStrapiPageContent.nodes;
  return (
    <Section className={className}>
      <div className="bg-dark text-emerald-50 py-24 md:py-32 px-6 rounded-4xl">
        <div className="max-w-5xl mx-auto">
          {testimonialHeader.map((data, index) => (
            <div key={index}>
              <TextLabel variant="light">{label}</TextLabel>
              <h3 className={`${h1} mt-6`}>{title}</h3>
            </div>
          ))}

          <div className="flex flex-col md:flex-row mt-16 md:mt-20 max-[962px]:gap-3 min-[963px]:gap-16 relative">
            <div className="absolute right-6 z-10 md:static md:z-0 rotate-90 mx-auto md:mx-0 md:rotate-0 flex w-12 grow-0 shrink-0 flex-col gap-2">
              <button
                className="py-2 md:border-y border-transparent md:hover:border-emerald-50"
                onClick={() => swiper?.slidePrev()}
              >
                <StaticImage
                  src="../../images/testimonial-arrow.svg"
                  alt="Previous slide arrow"
                  width={28}
                  height={28}
                />
              </button>
              <button
                className="py-2 md:border-y border-transparent md:hover:border-emerald-50"
                onClick={() => swiper?.slideNext()}
              >
                <StaticImage
                  className="rotate-180"
                  src="../../images/testimonial-arrow.svg"
                  alt="Next slide arrow"
                  width={28}
                  height={28}
                />
              </button>
            </div>
            <div className="flex grow max-w-full md:max-h-80 min-[888px]:w-[88%] min-[963px]:w-[67%] min-[1320px]:w-auto">
              <Swiper
                direction={swiperDirection}
                className={swiperDirection === 'vertical' ? 'h-[350px]' : 'h-full'}
                loop
                autoHeight={true}
                spaceBetween={50}
                slidesPerView={1}
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: true,
                }}
                modules={[Autoplay]}
                onSwiper={(swiper) => setSwiper(swiper)}
              >
                {testimonials.map((testimonial, index) => (
                  <SwiperSlide key={index}>
                    <div className={slideContainer}>
                      <div className={avatarContainer}>
                        <GatsbyImage
                          image={testimonial.avatar.localFile.childImageSharp.gatsbyImageData}
                          alt={testimonial.avatar.alternativeText}
                        />
                      </div>
                      <div
                        className={`${testimonialContainer} min-[1320px]:w-[auto] min-[963px]:w-[67%]`}
                      >
                        <h3 className={h3}>{testimonial.content}</h3>
                        <div className={clientInfoContainer}>
                          <h5 className={h5}>
                            {testimonial.name} {testimonial.position}
                          </h5>
                          <div className="mt-6 md:mt-0">
                            <img
                              src={testimonial.clientLogo.localFile.publicURL}
                              alt={testimonial.clientLogo.alternativeText}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Testimonails;
